import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from '@mui/material';
import NewWcnTable, { DataTableColumn, DataTablePagination } from '@/components/datatable/NewWcnTable.tsx';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rfidTagService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import EditorActions from '@/components/EditorActions.tsx';
import { DuplicateRfidTag } from './types.ts';

interface RfidTagFileImporterEditorProps {
    item: {
        products: any[];
        productTypeId: string;
        selectedFile: File;
    };
    onCancel: () => void;
    onChanged: () => void;
}

const RfidTagFileImporterEditor: React.FC<RfidTagFileImporterEditorProps> = ({ item, onCancel, onChanged }) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();
    const [loading, setLoading] = useState(false);
    const [skipList, setSkipList] = useState<DuplicateRfidTag[]>([]);
    const [overwriteList, setOverwriteList] = useState<DuplicateRfidTag[]>([]);
    const [duplicateRfidTags, setDuplicateRfidTags] = useState<DuplicateRfidTag[]>([]);
    const formRef = useRef<FormContainerRef>(null);

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        if (!isMountedRef.current) return;

        setLoading(true);
        const formData = new FormData();
        formData.append('file', item.selectedFile);

        try {
            const data = await rfidTagService.checkRfidTagDuplicates(formData);

            if (isMountedRef.current) {
                const duplicates = Array.isArray(data) ? data : [];
                setDuplicateRfidTags(duplicates);
            }
        } catch (error) {
            if (isMountedRef.current) {
                notifyErrorResponse(error);
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [item.selectedFile]);

    useEffect(() => {
        const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, load);
        load();

        return () => {
            unsubscribeReload();
        };
    }, [load]);

    const handleItemChanged = useCallback((action: string, rfidTag: DuplicateRfidTag) => {
        if (action === 'skip') {
            setSkipList((prev) => {
                const skipIndex = prev.findIndex((x) => x.id === rfidTag.id);

                if (skipIndex !== -1) {
                    // Remove from skip list
                    return prev.filter((_, i) => i !== skipIndex);
                } else {
                    // Add to skip list and remove from overwrite list
                    setOverwriteList((prevOverwrite) => prevOverwrite.filter((x) => x.id !== rfidTag.id));
                    return [...prev, rfidTag];
                }
            });
        } else if (action === 'overwrite') {
            setOverwriteList((prev) => {
                const overwriteIndex = prev.findIndex((x) => x.id === rfidTag.id);

                if (overwriteIndex !== -1) {
                    // Remove from overwrite list
                    return prev.filter((_, i) => i !== overwriteIndex);
                } else {
                    // Add to overwrite list and remove from skip list
                    setSkipList((prevSkip) => prevSkip.filter((x) => x.id !== rfidTag.id));
                    return [...prev, rfidTag];
                }
            });
        }
    }, []);

    const handleSave = useCallback(async () => {
        if (!formValid) {
            return;
        }

        const formData = new FormData();
        formData.append('file', item.selectedFile);
        formData.append('productTypeId', item.productTypeId);
        formData.append('skipJson', JSON.stringify(skipList));
        formData.append('overwriteJson', JSON.stringify(overwriteList));

        setLoading(true);
        try {
            await rfidTagService.importRfidTags(formData);

            if (isMountedRef.current) {
                notifySuccess();
                onChanged();
            }
        } catch (error) {
            if (isMountedRef.current) {
                notifyErrorResponse(error);
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [item.selectedFile, item.productTypeId]);

    const unconfirmed = duplicateRfidTags.length - (skipList.length + overwriteList.length);
    const confirmDialogHeader =
        unconfirmed > 0
            ? t('rfidTagsManualInputRequired', { count: unconfirmed })
            : t('rfidTagsManualInputNotRequired');

    const formValid = true;

    const columns: DataTableColumn<DuplicateRfidTag>[] = [
        {
            text: t('id'),
            value: 'id',
            width: 100
        },
        {
            text: t('productType'),
            value: 'productId',
            width: 200,
            render: (row) => item.products?.find((x) => x.id === row.productId)?.name || ''
        },
        {
            text: t('actions'),
            value: 'actions',
            isAction: true,
            sortable: false,
            render: (row) => (
                <Box>
                    <Button
                        size="small"
                        variant="outlined"
                        color={skipList.some((x) => x.id === row.id) ? 'secondary' : 'primary'}
                        onClick={() => handleItemChanged('skip', row)}
                    >
                        {skipList.some((x) => x.id === row.id) ? t('skipped') : t('skip')}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color={overwriteList.some((x) => x.id === row.id) ? 'success' : 'primary'}
                        sx={{ ml: 1 }}
                        onClick={() => handleItemChanged('overwrite', row)}
                    >
                        {overwriteList.some((x) => x.id === row.id) ? t('overwritten') : t('overwrite')}
                    </Button>
                </Box>
            )
        }
    ];

    const pagination: DataTablePagination = {
        count: duplicateRfidTags.length,
        rowsPerPageOptions: [5, 10, 15]
    };

    return (
        <FormContainer formRef={formRef}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box className="editor-sidebar-actions" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <EditorActions onSave={handleSave} onCancel={onCancel} saveDisabled={loading || !formValid} />
                </Box>

                <Box sx={{ p: 2 }}>
                    {loading && (
                        <Box py={3}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && (
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>
                                    <i className="zmdi zmdi-view-list" style={{ marginRight: 8 }} />
                                    {t('general')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ width: '100%' }}>
                                    {duplicateRfidTags.length > 0 ? (
                                        <>
                                            <Typography sx={{ mb: 2 }}>{confirmDialogHeader}</Typography>
                                            <NewWcnTable<DuplicateRfidTag>
                                                columns={columns}
                                                data={duplicateRfidTags}
                                                pagination={pagination}
                                                noDataMessage={t('noDuplicatesFound')}
                                            />
                                        </>
                                    ) : (
                                        <Typography>{t('rfidTagsManualInputNotRequired')}</Typography>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </Box>
        </FormContainer>
    );
};

export default RfidTagFileImporterEditor;
